import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ImgWithSelection } from 'src/app/img-db/model/img-with-selection';
import { CategoryWithSelection } from '../model/category-with-selection';
import { ImgAnnotation } from '../model/img-annotation';
import { AnnotationStateService } from '../services/annotation-state.service';
import { ImgCategory } from '../model/img-category';
import { ColorService } from '../../shared/services/color.service';
import { filter, Subscription, switchMap, tap } from 'rxjs';
import { ImagesStorageService } from '../services/images-storage.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogData,
} from '../../shared/components/confirmation-modal/confirmation-dialog.component';
import {
    SwapCategoryDialogComponent,
    SwapCategoryDialogData,
} from './rename-category-dialog/swap-category-dialog.component';
import { nonNil } from '../../shared/utility';
import { KeyEventsService } from '../services/key-events.service';

@Component({
    selector: 'app-classificator-tree',
    templateUrl: './classificator-tree.component.html',
    styleUrls: ['./classificator-tree.component.scss'],
    standalone: false,
})
export class ClassificatorTreeComponent
    implements OnInit, OnDestroy, OnChanges
{
    selectedImage: ImgWithSelection;
    @Input() categories: ImgCategory[] = [];
    @Input() canEdit: boolean;
    @Output() categorySelected: EventEmitter<CategoryWithSelection> =
        new EventEmitter();
    @Output() categoryDeleted: EventEmitter<number> = new EventEmitter();

    annotations: ImgAnnotation[];
    classificatorTree = new Map<string, ClassificatorTreeNode>();
    isClassificatorTreeSet: boolean = false;
    subscriptions: Subscription[] = [];

    //contextMenu variables
    parentContextMenuOpenFor: number = null;
    childContextMenuOpenFor: { parentIdx: number; childIdx: number } = {
        parentIdx: null,
        childIdx: null,
    };

    onKeyDownLambda = (event: KeyboardEvent) => this.handleKeydownEvents(event);
    keydownEventsExist: boolean = false;

    constructor(
        public annotationStateService: AnnotationStateService,
        private colorService: ColorService,
        private storage: ImagesStorageService,
        private dialog: MatDialog,
        private imagesStorage: ImagesStorageService,
        private keyEventsService: KeyEventsService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.categories && this.annotations) {
            this.castAnnotationToClassificatorTree();
        } else if (changes.canEdit) {
            if (this.canEdit) {
                this.addKeydownEvents();
            } else {
                this.removeKeydownEvents();
            }
        }
    }

    ngOnInit() {
        this.subscriptions.push(
            this.storage.selectedImage$.subscribe((img: ImgWithSelection) => {
                this.selectedImage = img;
            })
        );

        // Hide the previous image's tree while annotations are being fetched.
        // Spinner visibility is also driven by $loading (template binding), so
        // both rely on a single source of truth and cannot get out of sync.
        // Resetting isClassificatorTreeSet from selectedImage$ / pageChanged$
        // would re-introduce the cache-hit race where $annotations emits
        // synchronously before selectedImage$.
        this.subscriptions.push(
            this.annotationStateService.$loading.subscribe(
                (isLoading: boolean) => {
                    if (isLoading) {
                        this.isClassificatorTreeSet = false;
                    }
                }
            )
        );

        this.subscriptions.push(
            this.annotationStateService.$annotations.subscribe(
                (annotations: ImgAnnotation[]) => {
                    this.annotations = annotations;
                    this.castAnnotationToClassificatorTree();
                }
            )
        );

        this.subscriptions.push(
            this.keyEventsService.$modalOpen.subscribe((isOpen: boolean) => {
                if (isOpen) {
                    this.removeKeydownEvents();
                } else {
                    this.addKeydownEvents();
                }
            })
        );
    }

    private addKeydownEvents() {
        if (!this.keydownEventsExist && this.canEdit) {
            document.addEventListener('keydown', this.onKeyDownLambda);
            this.keydownEventsExist = true;
        }
    }

    private removeKeydownEvents() {
        if (this.keydownEventsExist) {
            document.removeEventListener('keydown', this.onKeyDownLambda);
            this.keydownEventsExist = false;
        }
    }

    private handleKeydownEvents(event: KeyboardEvent) {
        if (event.key === 'Delete') {
            event.preventDefault();
            this.handleDeleteEvent();
        } else if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'k' || event.key === 'K')
        ) {
            event.preventDefault();
            this.handleCtrlKEvent(event);
        }
    }

    private handleDeleteEvent() {
        const selected = this.annotationStateService.selectedAnnotation;
        if (nonNil(selected)) {
            this.annotationStateService.removeAnnotation(selected.id);
        }
    }

    private handleCtrlKEvent(event: KeyboardEvent) {
        const selectedAnnotation =
            this.annotationStateService.selectedAnnotation;

        if (nonNil(selectedAnnotation)) {
            const category = this.categories.find(
                (cat) => cat.id === selectedAnnotation.categoryId
            );
            this.onSwapCategory(event, category.name, selectedAnnotation.id);
        }
    }

    toggleClassificatorDropdown(categoryName: string) {
        this.classificatorTree.get(categoryName).open =
            !this.classificatorTree.get(categoryName)?.open;
    }

    selectClassificator(category: ClassificatorTreeItem) {
        this.annotationStateService.selectAnnotation(category.annotationId);
    }

    deleteAnnotation(event, classificatorTreeItem: ClassificatorTreeItem) {
        this.onCloseContextMenu(event);
        if (classificatorTreeItem.annotationId === undefined) {
            return;
        }
        this.annotationStateService.removeAnnotation(
            classificatorTreeItem.annotationId
        );
    }

    deleteCategory(event, key: string) {
        this.onCloseContextMenu(event);
        this.openDeletionDialog(key)
            .afterClosed()
            .subscribe((confirmedAnnotationsToDelete) => {
                if (confirmedAnnotationsToDelete) {
                    this.annotationStateService.removeAllAnnotationsForCategory(
                        this.categories.find(
                            (category: ImgCategory) => category.name === key
                        ).id,
                        true
                    );
                }
            });
    }

    private openDeletionDialog(annotationsToDelete: string) {
        return this.dialog.open(ConfirmationDialogComponent, {
            data: {
                headerTranslationKey: 'imgDb.classificatorTree.dialog.header',
                confirmationQuestion: {
                    translationKey: 'imgDb.classificatorTree.dialog.question',
                },
                confirmButtonTranslationKey:
                    'imgDb.classificatorTree.dialog.confirmText',
                rejectButtonTranslationKey:
                    'imgDb.classificatorTree.dialog.cancelText',
                transferData: annotationsToDelete,
            } as ConfirmationDialogData,
            width: '550px',
        });
    }

    castAnnotationToClassificatorTree() {
        for (const key of this.classificatorTree.keys()) {
            this.classificatorTree.get(key).items = [];
        }

        this.annotations.forEach((annotation: ImgAnnotation) => {
            this.addNewItemToClassificatorTree(annotation);
        });

        //remove categories with 0 items
        for (const key of this.classificatorTree.keys()) {
            if (this.classificatorTree.get(key).items.length === 0) {
                this.classificatorTree.delete(key);
            }
        }
        this.isClassificatorTreeSet = true;
    }

    addNewItemToClassificatorTree(annotation: ImgAnnotation) {
        const category = this.categories.find(
            (cat) => cat.id === annotation.categoryId
        );

        if (!this.classificatorTree.get(category.name)) {
            this.classificatorTree.set(
                category.name,
                new ClassificatorTreeNode(false, [])
            );
        }

        const node: ClassificatorTreeNode = this.classificatorTree.get(
            category.name
        );

        node.items.push({
            annotationId: annotation.id,
            active: annotation.active,
        });
        node.open = annotation.active || node.open;
    }

    getColor(categoryName: string): string {
        return this.colorService.getColor(categoryName);
    }

    onOpenContextMenu(event, elementIdx: number) {
        event.stopPropagation();
        this.parentContextMenuOpenFor = elementIdx;
        this.childContextMenuOpenFor.parentIdx = null;
        this.childContextMenuOpenFor.childIdx = null;
    }

    onOpenChildContextMenu(event, parentIdx: number, childIdx: number) {
        event.stopPropagation();
        this.parentContextMenuOpenFor = null;
        this.childContextMenuOpenFor.parentIdx = parentIdx;
        this.childContextMenuOpenFor.childIdx = childIdx;
    }

    onCloseContextMenu(event) {
        event.stopPropagation();
        this.parentContextMenuOpenFor = null;
        this.childContextMenuOpenFor.parentIdx = null;
        this.childContextMenuOpenFor.childIdx = null;
    }

    onSwapCategory(event, currentName: string, annotationId?: string) {
        this.onCloseContextMenu(event);
        this.openSwapCategoryDialog(currentName)
            .afterClosed()
            .pipe(
                tap((_) => this.keyEventsService.triggerModalStatus(false)),
                filter(
                    (selectedCategoryName: string) =>
                        selectedCategoryName &&
                        currentName !== selectedCategoryName
                ),
                switchMap((selectedCategoryName) => {
                    const categoryId = this.categories.find(
                        (category: ImgCategory) =>
                            category.name === selectedCategoryName
                    ).id;

                    let annotationsIds: string[];

                    if (nonNil(annotationId)) {
                        annotationsIds = [annotationId];
                    } else {
                        annotationsIds = this.classificatorTree
                            .get(currentName)
                            .items.map(
                                (item: ClassificatorTreeItem) =>
                                    item.annotationId
                            );
                    }

                    this.annotationStateService.$annotations.value
                        .slice()
                        .forEach((annotation) => {
                            if (annotationsIds.includes(annotation.id)) {
                                const oldAnnotation =
                                    this.annotationStateService.deepCopyImgAnnotation(
                                        annotation
                                    );

                                const newAnnotation =
                                    this.annotationStateService.deepCopyImgAnnotation(
                                        annotation
                                    );
                                newAnnotation.categoryId = categoryId;

                                this.annotationStateService.updateAnnotation(
                                    newAnnotation,
                                    oldAnnotation
                                );
                            }
                        });

                    return this.annotationStateService.saveCommands(
                        true,
                        this.imagesStorage.selectedImage$.value?.id
                    );
                })
            )
            .subscribe();
    }

    openSwapCategoryDialog(currentName: string) {
        const dialogConfig = new MatDialogConfig<SwapCategoryDialogData>();
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            annotation: currentName,
            categories: this.categories.map((category) => category.name),
        };

        this.keyEventsService.triggerModalStatus(true);
        return this.dialog.open(SwapCategoryDialogComponent, dialogConfig);
    }

    ngOnDestroy() {
        this.removeKeydownEvents();
        this.subscriptions.forEach((s) => s.unsubscribe());
    }
}

class ClassificatorTreeNode {
    constructor(
        public open: boolean,
        public items: ClassificatorTreeItem[]
    ) {}
}

class ClassificatorTreeItem {
    annotationId: string;
    active: boolean;
}
